import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';

import { adminApi, type AuditActorType } from '../../services/adminApi';

const PAGE_SIZE = 25;

const ACTOR_TYPE_FILTERS: { value: '' | AuditActorType; label: string }[] = [
  { value: '', label: 'All actors' },
  { value: 'admin', label: 'Admin' },
  { value: 'advertiser', label: 'Advertiser' },
  { value: 'user', label: 'User' },
  { value: 'system', label: 'System' },
];

const ACTOR_COLOR: Record<AuditActorType, 'primary' | 'secondary' | 'info' | 'default'> = {
  admin: 'primary',
  advertiser: 'secondary',
  user: 'info',
  system: 'default',
};

function formatTimestamp(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function summarisePayload(payload: unknown): string {
  if (payload === null || payload === undefined) return '—';
  try {
    const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
    return text.length > 120 ? `${text.slice(0, 117)}…` : text;
  } catch {
    return '—';
  }
}

/**
 * Admin audit-log viewer (GitHub #12 area 5).
 *
 * Paginated, filterable read of the immutable audit log via
 * `GET /api/admin/audit-log`: filter by action substring, actor type, and a
 * created-at date range. Auth is enforced upstream by AdminLayout (redirects
 * non-admins); the server also re-checks admin role.
 *
 * Privacy: surfaces only the operational metadata already recorded on each
 * entry (actor, action, target entity, change payload, originating IP).
 */
export function AuditLogPage() {
  const [actionDraft, setActionDraft] = useState('');
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState<'' | AuditActorType>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'audit-log', { action, actorType, from, to, page }],
    queryFn: () =>
      adminApi.listAuditLog({
        action: action || undefined,
        actorType: actorType || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setAction(actionDraft.trim());
    setPage(1);
  }

  function onActorTypeChange(event: SelectChangeEvent<string>) {
    setActorType(event.target.value as '' | AuditActorType);
    setPage(1);
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Audit log
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Immutable record of admin and system actions.
      </Typography>

      <Paper
        component="form"
        onSubmit={onSubmit}
        variant="outlined"
        elevation={0}
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <TextField
          id="audit-action"
          label="Action"
          size="small"
          value={actionDraft}
          onChange={(e) => setActionDraft(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="audit-actor-type-label">Actor type</InputLabel>
          <Select
            labelId="audit-actor-type-label"
            id="audit-actor-type"
            value={actorType}
            label="Actor type"
            onChange={onActorTypeChange}
          >
            {ACTOR_TYPE_FILTERS.map((a) => (
              <MenuItem key={a.value || 'all'} value={a.value}>
                {a.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id="audit-from"
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          id="audit-to"
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <Button type="submit" variant="contained">
          Filter
        </Button>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading audit log" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load audit log: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Audit log">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No audit entries match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.entries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{formatTimestamp(entry.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={entry.actorType}
                          color={ACTOR_COLOR[entry.actorType]}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{entry.action}</TableCell>
                      <TableCell>
                        {entry.entityType ? (
                          <>
                            {entry.entityType}
                            {entry.entityId ? ` (${entry.entityId.slice(0, 8)}…)` : ''}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>
                        {summarisePayload(entry.payload)}
                      </TableCell>
                      <TableCell>{entry.ipAddress ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Audit log pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default AuditLogPage;
